import CloseIcon from '@mui/icons-material/Close';
import { Box, Card, CircularProgress, IconButton, Typography } from '@mui/material';
import React from 'react';
import { UploadProgress } from './types';
import { isWordDocument } from './utils';

interface DocumentUploadProgressCardProps {
  progress: UploadProgress;
  onRemove: (fileName: string) => void;
}

const DocumentUploadProgressCard: React.FC<DocumentUploadProgressCardProps> = ({
  progress,
  onRemove,
}) => {
  const isCompleted = progress.status === 'completed';
  const isUploading = progress.status === 'uploading';
  const isProcessing = progress.status === 'processing';
  const isPending = progress.status === 'pending';
  const isError = progress.status === 'error';
  const isDocx = isWordDocument(progress.fileName);

  const borderColor = isError
    ? 'error.main'
    : isDocx
      ? 'warning.main'
      : isCompleted
        ? 'success.main'
        : 'divider';

  const backgroundColor = isError
    ? 'error.light'
    : isDocx
      ? 'warning.light'
      : isCompleted
        ? 'success.light'
        : 'background.paper';

  return (
    <Card
      sx={{
        minWidth: 100,
        maxWidth: 180,
        width: 'calc(20% - 8px)',
        position: 'relative',
        border: '1px solid',
        borderColor,
        backgroundColor,
        padding: '0.5rem 0.75rem',
        minHeight: '2.8125rem',
        boxSizing: 'border-box',
        '& .MuiCardContent-root': {
          padding: '0 !important',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 20,
            minHeight: 20,
            flexShrink: 0,
          }}
        >
          {isPending && <CircularProgress size={20} thickness={4} />}
          {isUploading && (
            <CircularProgress
              size={20}
              variant="determinate"
              value={progress.progress}
              thickness={4}
            />
          )}
          {isProcessing && <CircularProgress size={20} thickness={4} />}
          {isCompleted && (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.625rem',
              }}
            >
              ✓
            </Box>
          )}
          {isError && (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'error.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.625rem',
              }}
            >
              ✕
            </Box>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: isCompleted ? 600 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: isError ? 'error.dark' : 'text.primary',
              fontSize: '0.75rem',
              lineHeight: 1.2,
              display: 'block',
            }}
          >
            {progress.fileName}
          </Typography>
          {isUploading && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.625rem',
                display: 'block',
                mt: 0.25,
              }}
            >
              {Math.round(progress.progress)}%
            </Typography>
          )}
          {isPending && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.625rem',
                display: 'block',
                mt: 0.25,
              }}
            >
              Waiting...
            </Typography>
          )}
          {isProcessing && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.625rem',
                display: 'block',
                mt: 0.25,
              }}
            >
              Processing...
            </Typography>
          )}
          {isError && (
            <Typography
              variant="caption"
              sx={{
                color: 'error.dark',
                fontSize: '0.625rem',
                display: 'block',
                mt: 0.25,
              }}
            >
              Failed
            </Typography>
          )}
        </Box>
        {!isUploading && !isPending && !isProcessing && (
          <IconButton
            size="small"
            onClick={() => onRemove(progress.fileName)}
            sx={{
              p: 0.25,
              minWidth: 20,
              width: 20,
              height: 20,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: '0.875rem' }} />
          </IconButton>
        )}
      </Box>
    </Card>
  );
};

export default DocumentUploadProgressCard;
